import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMediaQuery } from 'react-responsive'
import { toast, ToastContainer } from 'react-toastify'
import '../../CSS/SupportCallFeedback.css'
import customFetchData from '../../Components/Custom/customFetchData'
import { globalState } from '../../GlobalState/globalState'
import fiverating from '../../Assets/fiverating.png'
const FEEDBACK_OPTIONS = [
    { key: 1, label: 'Bad', emoji: '😠', color: '#ffd9d9' },
    { key: 2, label: 'Poor', emoji: '☹️', color: '#ffe3c2' },
    { key: 3, label: 'Okay', emoji: '😐', color: '#fff2b8' },
    { key: 4, label: 'Good', emoji: '🙂', color: '#dcf5d1' },
    { key: 5, label: 'Great', emoji: '😍', color: '#ffd6e5' },
]

export default function SupportCallFeedback(props) {
    const navigate = useNavigate()
    const paramsLocation = useLocation()
    const isMobileView = useMediaQuery({ query: '(max-width: 760px)' })
    const [state, dispatch] = useContext(globalState)

    const [selectedRating, setSelectedRating] = useState('')
    const [submitted, setSubmitted] = useState(false)
    const [loading, setLoading] = useState(false)
    const [voiceCallPrid, setVoiceCallPrid] = useState(null)
    const [initialRatingData, setInitialRatingData] = useState(false)
    const [fetchingInitial, setFetchingInitial] = useState(false)
    const [idNotFound, setIdNotFound] = useState(false)

    // Memoize banner image URL to avoid recreation on every render
    const bannerImageUrl = useMemo(() => {
        return isMobileView
            ? 'https://slash-images.s3.us-west-2.amazonaws.com/Images_New/1786534112137-lg.png'
            : 'https://slash-images.s3.us-west-2.amazonaws.com/Images_New/1786533504582-lg.png'
    }, [isMobileView])

    // Extract URL parameters using URLSearchParams (more efficient)
    useEffect(() => {
        const urlParams = new URLSearchParams(paramsLocation.search)
        const id = urlParams.get('id')
        if (id) {
            setVoiceCallPrid(id)
            fetchInitialRating(id)
        }
    }, [paramsLocation.search])

    // Fetch initial feedback data if it exists
    const fetchInitialRating = async (id) => {
        if (!id) return

        setFetchingInitial(true)
        try {
            const body = {
                voiceCallPrid: Number(id),
            }
            const url = `/website/fetchSupportFeedback`
            const method = 'POST'

            const response = await customFetchData(method, body, url, state, dispatch)
            console.log('Initial rating fetch response:', response)
            if (response.fetch && response.fetchedData) {
                const { data, ratingExists } = response.fetchedData

                if (ratingExists && data && data.SUPPORT_CALL_RATING) {
                    setSubmitted(true)
                    setInitialRatingData(ratingExists)
                }
            }
            else {
                setIdNotFound(true)
            }

        } catch (error) {
            console.error('Failed to fetch initial rating:', error)
        } finally {
            setFetchingInitial(false)
        }
    }



    // Memoized callback to submit feedback, accepts rating directly to avoid stale state
    const onSubmitHandler = useCallback(async (ratingValue) => {
        if (!ratingValue) {
            toast.error('Please select a rating')
            return
        }

        if (!voiceCallPrid || !Number.isInteger(Number(voiceCallPrid)) || Number(voiceCallPrid) <= 0) {
            toast.error('Call information is missing. Please try again.')
            return
        }

        setLoading(true)

        try {
            const numericRating = ratingValue

            // Validate rating is between 1-5
            if (!Number.isInteger(numericRating) || numericRating < 1 || numericRating > 5) {
                toast.error('Invalid rating selection')
                setLoading(false)
                return
            }

            // Build request body
            const body = {
                rating: numericRating,
                voiceCallPrid: Number(voiceCallPrid),
            }

            const url = '/website/submitSupportFeedback'
            const method = 'POST'

            // Call API
            const feedbackResponse = await customFetchData(method, body, url, state, dispatch)

            // Validate response
            if (feedbackResponse.fetch) {
                const checkResponse = feedbackResponse.fetchedData

                if (checkResponse && checkResponse.submitted && checkResponse.data) {
                    setSubmitted(true)
                    toast.success('Thank you! Your feedback has been submitted successfully.')
                } else if (checkResponse && checkResponse.badRequest) {
                    toast.error(checkResponse.errorMsg || 'Invalid feedback data. Please try again.')
                } else if (checkResponse && checkResponse.notFound) {
                    toast.error('Call record not found. Please try again.')
                } else {
                    toast.error('Unable to process feedback. Please try again.')
                }
            } else {
                toast.error('Failed to submit feedback. Please check your connection.')
            }
        } catch (error) {
            console.error('Feedback submission error:', error)
            toast.error('An error occurred while submitting feedback')
        } finally {
            setLoading(false)
        }
    }, [voiceCallPrid, state, dispatch])

    // Selecting an emoji immediately submits the rating
    const onSelectRating = useCallback((ratingValue) => {
        if (loading || submitted) return
        setSelectedRating(ratingValue)
        onSubmitHandler(ratingValue)
    }, [loading, submitted, onSubmitHandler])

    return (
        <div className="support_feedback_page">
            <ToastContainer
                position="bottom-center"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <div className="support_feedback_banner">
                <img className="support_feedback_banner_img" src={bannerImageUrl} alt="Support" />
                <div className="support_feedback_banner_text">
                    <p className="support_feedback_banner_title">Share Your Feedback</p>
                    <p className="support_feedback_banner_subtitle">Help us improve your support experience</p>
                </div>
            </div>
            {voiceCallPrid && !idNotFound && (
                <div className="support_feedback_card">
                    <div className="support_feedback_header_row">
                        <div className="support_feedback_header_left">
                            <div className="support_feedback_headset_icon">🎧</div>
                            <p className="support_feedback_title">Rate Your Support Experience</p>
                        </div>
                    </div>

                    {submitted ? (
                        initialRatingData ?
                            (
                                <p className="support_feedback_question">You have already submitted your feedback. Thank you!</p>
                            ) :
                            (
                                <p className="support_feedback_question">Thank you for your valuable feedback! We truly appreciate your input.</p>
                            )
                    ) : (
                        <>
                            <p className="support_feedback_question">How would you rate your support experience?</p>

                            <div className="support_feedback_emoji_row" role="group" aria-label="Rate your support experience">
                                {FEEDBACK_OPTIONS.map((option) => (
                                    <button
                                        key={option.key}
                                        className={`support_feedback_emoji_item ${selectedRating === option.key ? 'selected' : ''}`}
                                        onClick={() => onSelectRating(option.key)}
                                        type="button"
                                        disabled={loading}
                                        aria-pressed={selectedRating === option.key}
                                        aria-label={`Rate ${option.label}`}
                                    >
                                        <div className="support_feedback_emoji_circle" style={{ backgroundColor: option.color }}>
                                            {option.emoji}
                                        </div>
                                        <span className="support_feedback_emoji_label">{option.label}</span>
                                    </button>
                                ))}
                            </div>
                            {loading && <p className="support_feedback_submitting_text" style={{ textAlign: 'center', paddingTop: 20 }}>Submitting...</p>}
                        </>
                    )}
                </div>
            )}
        </div>
    )
}
